// Selections slice — tracks which products/options the user has chosen and
// the server-computed preliminary estimate.
//
// Slice shape:
//   items: [{ productId: number, optionIds: number[] }]
//   estimate: { data: { low, high } | null, loading: boolean, error: string|null }
//
// optionIds is always an array. Products with `selection_mode === 'single'`
// keep at most one id in it; 'multiple' products may hold several. The pricing
// engine sums every selected optionId regardless of mode.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../api/public-client.js';

// Debounce: only fire the estimate request after 300 ms of quiet.
let estimateTimer = null;

export const fetchEstimate = createAsyncThunk(
  'selections/fetchEstimate',
  async ({ geometryToken, items }, { rejectWithValue }) => {
    try {
      if (!geometryToken || items.length === 0) return null;
      const selections = items.map((item) => (
        item.optionIds.length > 0
          ? { productId: item.productId, optionIds: item.optionIds }
          : { productId: item.productId }
      ));
      return await apiFetch('/api/pricing/estimate', {
        method: 'POST',
        body: { geometryToken, selections },
      });
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

// Dispatched by UI components after any selection change.
// Schedules a debounced estimate fetch so rapid checkbox toggling doesn't
// spam the server.
export const scheduleEstimate = (geometryToken) => (dispatch, getState) => {
  clearTimeout(estimateTimer);
  estimateTimer = setTimeout(() => {
    const { items } = getState().selections;
    dispatch(fetchEstimate({ geometryToken, items }));
  }, 300);
};

const selectionsSlice = createSlice({
  name: 'selections',
  initialState: {
    items: [],
    estimate: { data: null, loading: false, error: null },
  },
  reducers: {
    // Toggle a product on or off. Removing a product also clears its options.
    toggleProduct(state, { payload: productId }) {
      const idx = state.items.findIndex((i) => i.productId === productId);
      if (idx === -1) {
        state.items.push({ productId, optionIds: [] });
      } else {
        state.items.splice(idx, 1);
      }
      // Reset estimate so stale values are never shown between fetches.
      state.estimate.data = null;
    },
    // Toggle an option for a product. `mode` decides the behavior:
    //   'single'   → the option replaces any previously chosen one (radio).
    //   'multiple' → the option is added/removed from the set (checkbox).
    toggleProductOption(state, { payload: { productId, optionId, mode } }) {
      const item = state.items.find((i) => i.productId === productId);
      if (!item) return;
      if (mode === 'multiple') {
        item.optionIds = item.optionIds.includes(optionId)
          ? item.optionIds.filter((id) => id !== optionId)
          : [...item.optionIds, optionId];
      } else {
        item.optionIds = item.optionIds[0] === optionId ? [] : [optionId];
      }
      state.estimate.data = null;
    },
    // Clear all selections (e.g. on area type change).
    clearSelections(state) {
      state.items = [];
      state.estimate = { data: null, loading: false, error: null };
    },
    // Replace the whole selection set (e.g. restoring a saved draft). Only keeps
    // well-formed { productId, optionIds: [] } entries with integer ids.
    setSelections(state, { payload }) {
      state.items = (Array.isArray(payload) ? payload : [])
        .map((it) => ({
          productId: Number(it?.productId),
          optionIds: Array.isArray(it?.optionIds)
            ? it.optionIds.map(Number).filter(Number.isInteger)
            : [],
        }))
        .filter((it) => Number.isInteger(it.productId));
      state.estimate = { data: null, loading: false, error: null };
    },
    // Drop the current estimate without touching selections — used when the
    // geometry changes and the old price no longer applies.
    resetEstimate(state) {
      state.estimate.data = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEstimate.pending, (state) => {
        state.estimate.loading = true;
        state.estimate.error = null;
      })
      .addCase(fetchEstimate.fulfilled, (state, { payload }) => {
        state.estimate.loading = false;
        state.estimate.data = payload;
      })
      .addCase(fetchEstimate.rejected, (state, { payload }) => {
        state.estimate.loading = false;
        state.estimate.error = payload || 'Failed to compute estimate';
      });
  },
});

export const {
  toggleProduct, toggleProductOption, clearSelections, resetEstimate, setSelections,
} = selectionsSlice.actions;
export default selectionsSlice.reducer;
